"use client";

import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useSelector } from "react-redux";
import { Rocket } from "lucide-react";
import { TRIAL_ENGINE_ID } from "@/config/engines";
import { useAuthModal } from "@/hooks/useAuthModal";
import { isAuthenticated as hasAuthSession } from "@/utils/authUtils";
import apiService from "@/utils/apiService";
import { isEligibleForTrialDisplay } from "@/lib/trialEligibilityPayload";

const trialBtnClass =
  "inline-flex w-fit self-start items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:from-cyan-400 hover:to-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60 sm:px-5 sm:py-3";

function isUserLoggedIn(reduxAuthenticated) {
  return Boolean(reduxAuthenticated) || hasAuthSession();
}

export default function VolumeBotHeroCta() {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { openLoginModal } = useAuthModal();
  const [showTrialCta, setShowTrialCta] = useState(true);

  useEffect(() => {
    if (!isUserLoggedIn(isAuthenticated)) {
      setShowTrialCta(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getTrialEligibility();
        if (!cancelled) {
          setShowTrialCta(isEligibleForTrialDisplay(data));
        }
      } catch {
        if (!cancelled) setShowTrialCta(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const handleLaunchFreeTrial = useCallback(() => {
    const qs = new URLSearchParams(
      typeof window !== "undefined" ? window.location.search : "",
    );
    qs.set("engine", TRIAL_ENGINE_ID);
    qs.set("dl", String(Date.now()));
    const path = pathname || "/volume-bot";
    router.push(`${path}?${qs.toString()}#create-bot-step-1-config`, {
      scroll: false,
    });
    if (!isUserLoggedIn(isAuthenticated)) {
      openLoginModal();
    }
  }, [isAuthenticated, openLoginModal, pathname, router]);

  if (!showTrialCta) return null;

  return (
    <div className="flex flex-col gap-3 pt-3 sm:flex-row sm:flex-wrap sm:items-center sm:pt-4">
      <button
        type="button"
        onClick={handleLaunchFreeTrial}
        className={trialBtnClass}
      >
        <Rocket className="h-4 w-4 shrink-0" aria-hidden />
        Launch Free Trial Bot
      </button>
    </div>
  );
}
